import React, { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import dayjs from "dayjs";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import { PaymentType, UserType } from "../../../utils/enum";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import EditScheduleModal from "../components/EditScheduleModal";

const Schedule = ({
  currentStatus,
  schedule,
  minimumHours,
  paymentType,
  id,
  authRole,
  hasUpdateAccess,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Customers see the scheduling signals — ready to go, on the way, running late
  // — but not the engineer's on-site clock. Check in/out drive billing and are
  // between the client and the engineer.
  const isCustomer = authRole === UserType.CUSTOMER;
  return (
    <BaseCard className="pt-4 pb-6 xl:px-16 px-5 rounded-lg !mb-[0px] h-full" elevation={1}>
      <div className="border-b mb-4 flex items-center pb-1 justify-between">
        <div className="flex items-center">
          <h6 className="text-dark mr-2">Schedule</h6>
          {(authRole === UserType.CLIENT || authRole === UserType.CUSTOMER) &&
            hasUpdateAccess &&
            ["DRAFT", "PENDING_APPROVAL", "PUBLISHED", "ASSIGNED"].includes(currentStatus) && (
              <i
                className="fa-light fa-pencil text-gray-900 cursor-pointer"
                onClick={() => setIsModalOpen(true)}
              ></i>
            )}
        </div>

        <div className="flex items-center bg-[#F2FAFF] py-1 px-4 rounded-md mb-2">
          <i className="fa-solid fa-circle-exclamation text-primary"></i>
          <p className=" ml-3 font-normal">Fixed Start</p>
        </div>
      </div>
      <div>
        <div className="grid grid-cols-2 mt-6">
          <p>Start at a specific date and time</p>
          <p className="font-normal">
            {dayjs.utc(schedule.orderStartDate).format("dddd, DD/MM/YYYY")} at{" "}
            {dayjs
              .utc(schedule.orderStartTime)
              // .tz(schedule.timeZone)
              .format("hh:mm A")}{" "}
            ({getTimeZoneAbbreviation(schedule.timeZone)})
          </p>
        </div>

        {/* <p className="mt-8">
          Updated by <span className="text-primary">Colin Regan</span>
           on 8/12/2024 at 1:38 PM(EDT)
        </p> */}
      </div>
      <div className="border-t my-6"></div>
      {schedule.confirmedWorkOrderAt ? (
        <div>
          <div className="grid grid-cols-2 mt-6">
            <p>Ready to go Time</p>
            <p className="font-normal">
              {dayjs(schedule.confirmedWorkOrderAt)
                .tz(schedule?.timeZone)
                .format("dddd, DD/MM/YYYY")}{" "}
              at {dayjs(schedule.confirmedWorkOrderAt).tz(schedule.timeZone).format("h:mm A")}
              {/* Monday, 8/19/2024 at 9:00 AM (EDT) */}
            </p>
            <p></p>
            <p className="font-normal">
              (
              {/* {getTimeZoneAbbreviation(
                Intl.DateTimeFormat().resolvedOptions().timeZone
              )} */}
              {getTimeZoneAbbreviation(schedule.timeZone)})
            </p>
          </div>

          {/* <p className="mt-8">
            Confirmed by <span className="text-primary">Ahmad Warsame</span>
             on 8/12/2024 at 1:38 PM(EDT)
          </p> */}

          {schedule.onTheWayWorkOrderAt && (
            <div className="grid grid-cols-2 mt-6">
              <p>On the way Time</p>
              <p className="font-normal">
                {dayjs(schedule.onTheWayWorkOrderAt)
                  .tz(schedule?.timeZone)
                  .format("dddd, DD/MM/YYYY")}{" "}
                at {dayjs(schedule.onTheWayWorkOrderAt).tz(schedule.timeZone).format("h:mm A")}
              </p>
              <p></p>
              <p className="font-normal">({getTimeZoneAbbreviation(schedule.timeZone)})</p>
            </div>
          )}

          {schedule.runningLateWorkOrderAt && (
            <div className="grid grid-cols-2 mt-6">
              <div className="flex items-center gap-2">
                <p>Running Late Time</p>
                {schedule?.runningLateWorkOrderReason && (
                  <BaseTooltip
                    placement="bottom"
                    interactive={true}
                    content={
                      <div>
                        <p className="text-white">Reason: {schedule?.runningLateWorkOrderReason}</p>
                      </div>
                    }
                  >
                    <i className="fa-solid fa-circle-info text-xs cursor-pointer text-gray-400"></i>
                  </BaseTooltip>
                )}
              </div>
              {/* Date & time in second column */}
              <p className="font-normal">
                {dayjs(schedule.runningLateWorkOrderAt)
                  .tz(schedule?.timeZone)
                  .format("dddd, DD/MM/YYYY")}{" "}
                at {dayjs(schedule.runningLateWorkOrderAt).tz(schedule.timeZone).format("h:mm A")}
              </p>
              {/* Empty spacer for alignment (keeps grid 2x2 consistent) */}
              <p></p>
              {/* Timezone aligned in second column */}
              <p className="font-normal">({getTimeZoneAbbreviation(schedule.timeZone)})</p>
            </div>
          )}
          {!isCustomer && schedule.checkInAt && (
            <div className="grid grid-cols-2 mt-6">
              <p>Check In Time</p>
              <p className="font-normal">
                {dayjs(schedule?.checkInAt).tz(schedule?.timeZone).format("dddd, DD/MM/YYYY")} at{" "}
                {dayjs(schedule?.checkInAt).tz(schedule?.timeZone).format("hh:mm A")}
              </p>
              <p></p>
              <p className="font-normal">({getTimeZoneAbbreviation(schedule?.timeZone)})</p>
            </div>
          )}
          {!isCustomer && schedule.checkOutAt && (
            <div className="grid grid-cols-2 mt-6">
              <p>Check Out Time</p>
              <p className="font-normal">
                {dayjs(schedule?.checkOutAt).tz(schedule?.timeZone).format("dddd, DD/MM/YYYY")} at{" "}
                {dayjs(schedule?.checkOutAt).tz(schedule?.timeZone).format("hh:mm A")}
              </p>
              <p></p>
              <p className="font-normal">({getTimeZoneAbbreviation(schedule?.timeZone)})</p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-center font-normal italic">Ready to go has not been set</p>
      )}
      {isModalOpen && (
        <EditScheduleModal
          open={isModalOpen}
          close={() => setIsModalOpen(false)}
          workOrderData={{ id: id, ...schedule }}
        />
      )}
    </BaseCard>
  );
};

export default Schedule;
